import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, KeyRound, Loader2, PlugZap, ShieldCheck } from 'lucide-react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { Modal } from '@/shared/ui/Modal'
import { EmptyState, ErrorStrip, Forbidden, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import {
  AUTH_KIND_LABEL, CATEGORY_LABEL, CREDENTIAL_KIND_LABEL,
  createIntegration, listIntegrations, listProviders,
} from './api'
import { notifyIntegrationsChanged } from './navEntries'
import {
  PROVIDER_BLURB, PROVIDER_CATEGORY, PROVIDER_NOT_WIRED, PROVIDER_SETUP, providerLabel,
} from './providers'

/**
 * A provider that nobody has configured yet.
 *
 * The sidebar lists every provider the backend knows about, so every one of them
 * needs somewhere to land. This is that page, and it answers the only question
 * that matters before configuration: **what would turning this on give us, and
 * what would we have to supply?**
 *
 * It is deliberately not an empty version of the integration page. An
 * unconfigured provider has no traffic, no credentials and no health, so charts
 * and tabs would be furniture around nothing. What it does have is the catalogue
 * entry — capabilities, auth kind, required credentials, config keys — and that
 * is a genuinely useful screen on its own.
 *
 * ## Why registration lives here
 *
 * `POST /integrations` had no caller anywhere in the app. The registry could
 * only ever be empty, which is why the Integrations screen looked broken on a
 * fresh install: there was no route from "the backend supports eCitizen IPRS" to
 * "eCitizen IPRS is configured". Registering from the provider's own page is the
 * natural place — you have just read what it needs.
 *
 * Registration creates a **DRAFT**. It does not switch anything on: credentials
 * come next, on the integration's own page, and the backend enables nothing
 * until they are stored. Saying so on the button matters, because "Register"
 * next to a government identity service should never feel like it might start
 * sending real queries.
 */
export function ProviderPage() {
  const { code = '' } = useParams()
  const canRead = useCan('integration:read')
  const canCreate = useCan('integration:create')

  const providers = useLoad(() => listProviders(), [])
  const integrations = useLoad(() => listIntegrations(), [])
  const [registering, setRegistering] = useState(false)

  if (!canRead) return <Forbidden what="integrations" />

  const descriptor = providers.data?.find((p) => p.provider === code)
  const existing = (integrations.data ?? []).filter((i) => i.provider === code)
  const label = providerLabel(code)
  const blurb = PROVIDER_BLURB[code]
  const notWired = PROVIDER_NOT_WIRED[code]

  // Identity is (code, environment), so a provider registered in sandbox can
  // still be registered in production. Gating the button on "any registration
  // exists" would make the second environment permanently unreachable — and
  // this page is the only caller of POST /integrations in the app.
  const takenEnvironments = new Set<string>(existing.map((i) => i.environment))
  const freeEnvironments = ENVIRONMENTS.filter((e) => !takenEnvironments.has(e.code))
  // Never offer registration against a registry that has not loaded: `existing`
  // is empty during the fetch, which would present a configured provider as
  // unconfigured and let a second row be created for it.
  const canOffer = !integrations.loading && freeEnvironments.length > 0

  return (
    <div className="space-y-4">
      <SectionHeader
        title={label}
        description={
          // A provider that is not in the catalogue never gets a descriptor, so
          // keying this on `descriptor` alone leaves it saying "Loading…" for
          // ever underneath a "Not in the catalogue" panel.
          providers.loading
            ? 'Loading…'
            : descriptor
              ? `${code} · ${CATEGORY_LABEL[PROVIDER_CATEGORY[code] ?? 'OTHER']}`
                + (existing.length > 0 ? '' : ' · not configured')
              : 'Unknown provider'
        }
        right={
          <Link to="/integrations" className="erp-btn">
            <ArrowLeft className="h-3.5 w-3.5" /> Registry
          </Link>
        }
      />

      <ErrorStrip message={providers.error} />
      <ErrorStrip message={integrations.error} />

      {/* Said before the Register button, not after it. */}
      {notWired && (
        <Note tone="bad">
          <div className="space-y-1.5">
            <div>
              <strong>Registering {label} here does not switch it on.</strong> It is in the
              catalogue, but nothing in the app reads its credentials from the registry yet.
            </div>
            <div>
              {label} is configured in <strong>{notWired.where}</strong>. Change it there and
              restart the API.
            </div>
            <div>
              Its calls therefore do <strong>not</strong> appear in the integration call log —
              an empty log here says nothing about whether mail is working. The evidence is in{' '}
              <Link className="underline" to="/notifications?failedOnly=true">
                the {notWired.evidence}
              </Link>
              , which records every message, its status, the provider's error and how many
              attempts it has had.
            </div>
          </div>
        </Note>
      )}

      {/* Someone reached this URL for a provider that has since been registered.
          Send them onward rather than offering a second registration. */}
      {existing.length > 0 && (
        <Note tone="info">
          <div className="space-y-1.5">
            <div>
              {freeEnvironments.length === 0
                ? 'This provider is registered in every environment. Its configuration, credentials and report live on the integrations themselves:'
                : 'This provider is already registered. Its configuration, credentials and report live on the integration itself:'}
            </div>
            <div className="flex flex-wrap gap-2">
              {existing.map((i) => (
                <Link key={i.id} to={`/integrations/${i.id}`} className="erp-btn">
                  {i.displayName} <span className="text-muted-foreground">· {i.environment}</span>
                </Link>
              ))}
            </div>
          </div>
        </Note>
      )}

      {!providers.loading && !descriptor && (
        <EmptyState
          title="Not in the catalogue"
          hint={`The backend does not declare a provider called "${code}". Either it was retired, or the link is stale.`}
        />
      )}

      {descriptor && (
        <>
          {blurb && (
            <Panel title="What it does">
              <p className="text-erp-sm text-muted-foreground max-w-3xl">{blurb}</p>
            </Panel>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="What it can do" description="Capabilities the backend will accept for this provider">
              <ul className="space-y-1.5">
                {descriptor.capabilities.length === 0 ? (
                  <li className="text-erp-sm text-muted-foreground">None declared.</li>
                ) : (
                  [...descriptor.capabilities].sort().map((c) => (
                    <li key={c} className="text-erp-sm flex items-center gap-2">
                      <span className="h-1 w-1 rounded-full bg-current opacity-40 shrink-0" />
                      <code className="font-mono text-erp-xs">{c}</code>
                    </li>
                  ))
                )}
              </ul>
              <p className="mt-3 text-erp-xs text-muted-foreground">
                A capability outside this list is rejected at registration — the catalogue is the
                allowlist, not a suggestion.
              </p>
            </Panel>

            <Panel title="What it needs" description="Supplied after registration, on the Credentials tab">
              <dl className="space-y-2.5 text-erp-sm">
                <div className="flex items-baseline gap-2">
                  <dt className="text-erp-xs text-muted-foreground w-28 shrink-0">Authentication</dt>
                  <dd>{AUTH_KIND_LABEL[descriptor.authKind] ?? descriptor.authKind}</dd>
                </div>
                <div className="flex items-baseline gap-2">
                  <dt className="text-erp-xs text-muted-foreground w-28 shrink-0">Required</dt>
                  <dd className="flex flex-wrap gap-1.5">
                    {descriptor.requiredCredentials.length === 0
                      ? <span className="text-muted-foreground">Nothing — this provider is unauthenticated.</span>
                      : [...descriptor.requiredCredentials].sort().map((k) => (
                          <span key={k} className="px-1.5 py-0.5 rounded border border-border text-erp-xs">
                            {CREDENTIAL_KIND_LABEL[k] ?? humanise(k)}
                          </span>
                        ))}
                  </dd>
                </div>
                {descriptor.optionalCredentials.length > 0 && (
                  <div className="flex items-baseline gap-2">
                    <dt className="text-erp-xs text-muted-foreground w-28 shrink-0">Optional</dt>
                    <dd className="flex flex-wrap gap-1.5">
                      {[...descriptor.optionalCredentials].sort().map((k) => (
                        <span key={k} className="px-1.5 py-0.5 rounded border border-dashed border-border text-erp-xs text-muted-foreground">
                          {CREDENTIAL_KIND_LABEL[k] ?? humanise(k)}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
                {descriptor.configKeys.length > 0 && (
                  <div className="flex items-baseline gap-2">
                    <dt className="text-erp-xs text-muted-foreground w-28 shrink-0">Config keys</dt>
                    <dd className="flex flex-wrap gap-1.5">
                      {[...descriptor.configKeys].sort().map((k) => (
                        <code key={k} className="font-mono text-erp-xs px-1.5 py-0.5 rounded bg-muted">{k}</code>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
              <p className="mt-3 text-erp-xs text-muted-foreground inline-flex items-start gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 mt-px shrink-0" />
                <span>
                  Secrets are sealed on arrival and never returned — not to this screen, not to
                  anyone. What you see afterwards is a fingerprint.
                </span>
              </p>
            </Panel>
          </div>

          <Panel title="What it will record" description="Every call, whether it succeeds or not">
            <div className="text-erp-sm text-muted-foreground max-w-3xl space-y-2">
              <p>
                Once configured, each call writes a row: capability, outcome, HTTP status, latency,
                attempt number and an upstream reference — never the subject, the URL or the query
                string. Those rows are what the integration's Report tab charts.
              </p>
              <p>
                Some providers also record their own facets — for an identity lookup, whether the
                subject matched, and the gender and citizenship the registry returned. Which facets
                a provider declares is not exposed by the API, so they appear on the report once
                calls have actually been made rather than being listed here in advance.
              </p>
            </div>
          </Panel>

          {canOffer && (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                className="erp-btn erp-btn-primary"
                disabled={!canCreate}
                onClick={() => setRegistering(true)}
                title={canCreate ? undefined : 'Requires the integration:create authority'}
              >
                <PlugZap className="h-3.5 w-3.5" />
                {existing.length > 0
                  ? `Register ${freeEnvironments[0]!.label.toLowerCase()}`
                  : `Register ${label}`}
              </button>
              <span className="text-erp-xs text-muted-foreground">
                {canCreate
                  ? 'Creates a draft. Nothing is called upstream until credentials are stored.'
                  : 'You do not hold the authority to register an integration. Ask an administrator.'}
              </span>
            </div>
          )}
        </>
      )}

      {registering && descriptor && (
        <RegisterModal
          code={code}
          label={label}
          authKind={descriptor.authKind}
          capabilities={descriptor.capabilities}
          configKeys={descriptor.configKeys}
          available={freeEnvironments}
          onClose={() => setRegistering(false)}
        />
      )}
    </div>
  )
}

const ENVIRONMENTS = [
  { code: 'SANDBOX', label: 'Sandbox', hint: 'The provider’s test environment. Start here.' },
  { code: 'PRODUCTION', label: 'Production', hint: 'Live traffic against real records.' },
]

/**
 * The registration form.
 *
 * Everything the catalogue already knows — provider, auth kind, capabilities,
 * category — is sent without asking. Only the four things a person actually
 * decides are on the form: which environment, what to call it, the base URL if
 * it differs from the adapter's default, and a note.
 *
 * `code` is derived rather than typed. Identity is `(code, environment)`, so a
 * hand-typed code is a collision waiting to happen and carries no information
 * the provider and environment do not already carry.
 */
function RegisterModal({
  code, label, authKind, capabilities, configKeys, available, onClose,
}: {
  code: string
  label: string
  authKind: string
  capabilities: string[]
  /** The provider's declared keys — the only ones the service will accept. */
  configKeys: string[]
  /** Only the environments this provider is not already registered in. */
  available: typeof ENVIRONMENTS
  onClose: () => void
}) {
  const navigate = useNavigate()
  const setup = PROVIDER_SETUP[code] ?? {}
  const keys = [...configKeys].sort()
  const [environment, setEnvironment] = useState(available[0]?.code ?? 'SANDBOX')
  const [displayName, setDisplayName] = useState(label)
  const [baseUrl, setBaseUrl] = useState(setup.baseUrl ?? '')
  const [config, setConfig] = useState<Record<string, string>>(
    () => Object.fromEntries(keys.map((k) => [k, setup.config?.[k] ?? ''])))
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const slug = `${code.toLowerCase().replace(/_/g, '-')}-${environment.toLowerCase()}`
  const chosenEnv = available.find((e) => e.code === environment)

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      const created = await createIntegration({
        code: slug,
        environment,
        displayName: displayName.trim() || label,
        category: PROVIDER_CATEGORY[code] ?? 'OTHER',
        provider: code,
        authKind,
        baseUrl: baseUrl.trim() || null,
        capabilities,
        // Collected here rather than left for later. An integration registered
        // with an empty config is not merely incomplete — for eCitizen it is
        // silently broken: with no `token_url` the token exchange throws, the
        // adapter swallows it (an identity outage must never block
        // registration), and every lookup returns "not found" forever.
        config: Object.fromEntries(
          Object.entries(config).filter(([, v]) => v.trim() !== '').map(([k, v]) => [k, v.trim()])),
        rateLimit: {},
        scopeUnitId: null,
        notes: notes.trim() || null,
      })
      // The sidebar is mounted above every route and will not re-render on its
      // own — without this the integration you just created is missing from the
      // list you are about to be sent to.
      notifyIntegrationsChanged()
      navigate(`/integrations/${created.id}?tab=credentials`)
    } catch (e) {
      setError(explain(e))
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Register ${label}`}
      description="Creates a draft integration. Credentials come next; nothing is called upstream until they are stored."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary" disabled={saving} onClick={() => void submit()}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Register
          </button>
        </>
      }
    >
      <ErrorStrip message={error} />

      <div className="space-y-3">
        <div>
          <span className="text-erp-xs text-muted-foreground">Environment</span>
          <div className="mt-1 flex gap-2">
            {available.map((e) => (
              <button
                key={e.code}
                type="button"
                onClick={() => setEnvironment(e.code)}
                className={cn(
                  'flex-1 px-3 py-2 rounded-md border text-erp-sm text-left',
                  environment === e.code
                    ? 'border-chocolate bg-chocolate-soft/30 font-medium'
                    : 'border-border hover:bg-muted',
                )}
              >
                {e.label}
              </button>
            ))}
          </div>
          {chosenEnv && <p className="text-erp-xs text-muted-foreground mt-1">{chosenEnv.hint}</p>}
        </div>

        <label className="block">
          <span className="text-erp-xs text-muted-foreground">Display name</span>
          <input
            className="erp-input w-full mt-1"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={label}
          />
        </label>

        <label className="block">
          <span className="text-erp-xs text-muted-foreground">Base URL</span>
          <input
            className="erp-input w-full mt-1 font-mono text-erp-xs"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://…"
          />
          <p className="text-erp-xs text-muted-foreground mt-1">
            The host the adapter calls. Required for anything that makes outbound requests —
            eCitizen returns "not configured" without it.
          </p>
        </label>

        {keys.length > 0 && (
          <div>
            <span className="text-erp-xs text-muted-foreground">
              Configuration <span className="opacity-70">— only keys this provider declares</span>
            </span>
            <div className="mt-1 space-y-2.5">
              {keys.map((k) => (
                <div key={k}>
                  <label className="flex items-center gap-2">
                    <code className="font-mono text-erp-xs w-28 shrink-0 truncate" title={k}>{k}</code>
                    <input
                      className="erp-input flex-1 min-w-0 font-mono text-erp-xs"
                      value={config[k] ?? ''}
                      onChange={(e) => setConfig((c) => ({ ...c, [k]: e.target.value }))}
                      placeholder="Not set"
                    />
                  </label>
                  {setup.hints?.[k] && (
                    <p className="text-erp-xs text-muted-foreground mt-0.5 pl-[calc(7rem+0.5rem)]">{setup.hints[k]}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <label className="block">
          <span className="text-erp-xs text-muted-foreground">Notes <span className="opacity-70">(optional)</span></span>
          <textarea
            className="erp-input w-full mt-1 min-h-[56px]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Who owns the account, where the contract lives."
          />
        </label>

        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-erp-xs text-muted-foreground space-y-1">
          <div className="flex items-center gap-2">
            <KeyRound className="h-3.5 w-3.5 shrink-0" />
            <span>Sent from the catalogue, not from this form:</span>
          </div>
          <div className="pl-5">
            code <code className="font-mono">{slug}</code> · provider <code className="font-mono">{code}</code>
            {' '}· auth {AUTH_KIND_LABEL[authKind] ?? authKind} · {capabilities.length} capabilit
            {capabilities.length === 1 ? 'y' : 'ies'}
          </div>
        </div>
      </div>
    </Modal>
  )
}
